import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {IconKey, IconLogout, IconBulb, IconSettings, IconSun, IconMoon, IconX, IconChevronLeft, IconPlus, IconRobot, IconBuilding} from '@tabler/icons-react';
import {isClientAdmin} from '../utils/roles';
import {useTheme} from '../hooks/useTheme';
import {Application, Project} from '../types';
import {ChangePasswordModal} from './ChangePasswordModal';
import {storage} from '../utils/localStorage';
import {useSidebar} from '../contexts';
import packageJson from '../../package.json';
import './Sidebar.css';

interface SidebarProps {
    onItemSelect?: (item: string) => void;
    onLogout?: () => void;
    /** Full admins may switch the tenant they work in; absent for everyone else. */
    onSwitchTenant?: () => void;
    user?: {
        firstName: string;
        lastName: string;
        email: string;
        tenantName?: string;
    } | null;
    onApplicationSelect?: (application: Application) => void;
    applications?: Application[];
    onLogoClick?: () => void;
    selectedApplicationId?: string;
    loadingApplications?: boolean;
}

interface ProjectWithApps extends Project {
    applications: Application[];
}

export const Sidebar: React.FC<SidebarProps> = ({
                                                    onItemSelect,
                                                    onLogout,
                                                    onSwitchTenant,
                                                    user,
                                                    onApplicationSelect,
                                                    applications: propApplications,
                                                    onLogoClick,
                                                    selectedApplicationId,
                                                    loadingApplications = false
                                                }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [projects, setProjects] = useState<ProjectWithApps[]>([]);
    const [applications, setApplications] = useState<Application[]>([]);
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);
    const {theme, toggleTheme} = useTheme();
    const {isMobileOpen, closeMobile, isDesktopCollapsed, toggleDesktop} = useSidebar();

    useEffect(() => {
        if (!isMobileOpen) return;

        const scrollY = window.scrollY;
        const body = document.body;
        body.classList.add('v5-side-locked');
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';

        return () => {
            body.classList.remove('v5-side-locked');
            body.style.position = '';
            body.style.top = '';
            body.style.left = '';
            body.style.right = '';
            body.style.width = '';
            window.scrollTo(0, scrollY);
        };
    }, [isMobileOpen]);

    useEffect(() => {
        if (propApplications && propApplications.length > 0) {
            setApplications(propApplications);
            groupApplicationsByProject(propApplications);
        }
    }, [propApplications]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
                setShowUserMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const groupApplicationsByProject = (apps: Application[]) => {
        const projectsMap = new Map<string, ProjectWithApps>();

        apps.forEach((app: any) => {
            const projectId = app.project.id;
            if (!projectsMap.has(projectId)) {
                projectsMap.set(projectId, {
                    id: app.project.id,
                    name: app.project.displayName || app.project.name,
                    code: app.project.code,
                    sortOrder: app.project.sortOrder,
                    applications: []
                });
            }
            projectsMap.get(projectId)?.applications.push(app);
        });

        const sortedProjects = Array.from(projectsMap.values())
            .map(project => ({
                ...project,
                applications: [...project.applications].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            }))
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

        setProjects(sortedProjects);
        // open all sections by default
        setOpenSections(prev => {
            const next = {...prev};
            sortedProjects.forEach(p => {
                if (next[String(p.id)] === undefined) next[String(p.id)] = true;
            });
            return next;
        });
    };

    const toggleSection = (id: string) => {
        setOpenSections(prev => ({...prev, [id]: !prev[id]}));
    };

    const userInitials = user
        ? (user.firstName && user.lastName
            ? user.firstName.charAt(0).toUpperCase() + user.lastName.charAt(0).toUpperCase()
            : (user.email?.charAt(0).toUpperCase() || '?'))
        : '?';

    return (
        <>
            {isMobileOpen && (
                <div
                    className="v5-side-backdrop"
                    onClick={closeMobile}
                    aria-hidden="true"
                />
            )}
            <aside className={`v5-side ${isMobileOpen ? 'is-mobile-open' : ''} ${isDesktopCollapsed ? 'is-desktop-collapsed' : ''}`}>
            <button
                type="button"
                className="v5-side-close"
                onClick={closeMobile}
                aria-label="Zavrieť menu"
            >
                <IconX size={18}/>
            </button>
            <button
                type="button"
                className="v5-side-collapse"
                onClick={toggleDesktop}
                aria-label="Skryť menu"
                title="Skryť menu"
            >
                <IconChevronLeft size={16}/>
            </button>
            <button
                className="v5-brand"
                onClick={() => {
                    closeMobile();
                    onLogoClick?.();
                }}
                type="button"
            >
                <div className="v5-brand-meta">
                    <span className="v5-brand-name">flowy<span className="v5-brand-dot">.</span></span>
                    {user?.tenantName && <span className="v5-brand-org">{user.tenantName}</span>}
                </div>
            </button>

            {loadingApplications ? (
                <div className="v5-side-loading">
                    <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Načítavam...</span>
                    </div>
                </div>
            ) : (
                projects.map(project => {
                    const projectId = String(project.id);
                    const isOpen = openSections[projectId] !== false;
                    return (
                        <div
                            key={projectId}
                            className={`v5-nav-section ${isOpen ? 'is-open' : 'is-closed'}`}
                        >
                            <button
                                type="button"
                                className="v5-nav-label"
                                onClick={() => toggleSection(projectId)}
                                aria-expanded={isOpen}
                            >
                                <span>{project.name}</span>
                                <svg
                                    className="v5-nav-caret"
                                    width="11"
                                    height="11"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M4.5 3L7.5 6L4.5 9"/>
                                </svg>
                            </button>
                            {isOpen && (
                                <div className="v5-nav-items">
                                    {project.applications.map(app => {
                                        const fullApp = applications.find(a => a.id === app.id);
                                        const isActive = String(app.id) === selectedApplicationId;
                                        return (
                                            <button
                                                type="button"
                                                key={app.id}
                                                data-e2e={`app:${app.id}`}
                                                className={`v5-nav-item ${isActive ? 'is-active' : ''}`}
                                                onClick={() => {
                                                    closeMobile();
                                                    onItemSelect?.(`app-${app.id}`);
                                                    if (fullApp && onApplicationSelect) {
                                                        onApplicationSelect(fullApp);
                                                    }
                                                }}
                                            >
                                                <span>{app.displayName || app.name}</span>
                                            </button>
                                        );
                                    })}
                                    {project.code === 'statistics' && isClientAdmin() && (
                                        <button
                                            type="button"
                                            className="v5-nav-item"
                                            onClick={() => {
                                                closeMobile();
                                                navigate('/statistics/new');
                                            }}
                                        >
                                            <span><IconPlus size={13} className="me-1"/>Pridať štatistiku</span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })
            )}

            {!loadingApplications && isClientAdmin() && !projects.some(p => p.code === 'statistics') && (
                <div className="v5-nav-section is-open">
                    <button type="button" className="v5-nav-label" aria-expanded={true}>
                        <span>Štatistiky</span>
                    </button>
                    <div className="v5-nav-items">
                        <button
                            type="button"
                            className="v5-nav-item"
                            onClick={() => {
                                closeMobile();
                                navigate('/statistics/new');
                            }}
                        >
                            <span><IconPlus size={13} className="me-1"/>Pridať štatistiku</span>
                        </button>
                    </div>
                </div>
            )}

            {!loadingApplications && isClientAdmin() && (
                <div className="v5-nav-section is-open">
                    <button type="button" className="v5-nav-label" aria-expanded={true}>
                        <span>AI boti</span>
                    </button>
                    <div className="v5-nav-items">
                        <button
                            type="button"
                            className={`v5-nav-item${location.pathname === '/ai-bots' ? ' is-active' : ''}`}
                            onClick={() => {
                                closeMobile();
                                navigate('/ai-bots');
                            }}
                        >
                            <span><IconRobot size={13} className="me-1"/>AI boti</span>
                        </button>
                    </div>
                </div>
            )}

            <div className="v5-side-footer">
                {process.env.REACT_APP_IDEAS_URL && (
                    <a
                        href="#"
                        className="v5-side-ideas"
                        onClick={(e) => {
                            e.preventDefault();
                            window.open(`${process.env.REACT_APP_IDEAS_URL}/auth?token=${storage.getItem('token') || ''}`, '_blank');
                        }}
                    >
                        <IconBulb size={14}/>
                        Nápady
                    </a>
                )}

            <div className="v5-side-bottom" ref={userMenuRef}>
                {showUserMenu && (
                    <div className="v5-side-user-menu">
                        <button
                            type="button"
                            className="v5-side-menu-item"
                            onClick={() => {
                                toggleTheme();
                            }}
                        >
                            {theme === 'dark' ? <IconSun size={14}/> : <IconMoon size={14}/>}
                            {theme === 'dark' ? 'Svetlý režim' : 'Nočný režim'}
                        </button>
                        <button
                            type="button"
                            className="v5-side-menu-item"
                            onClick={() => {
                                setShowUserMenu(false);
                                setShowChangePassword(true);
                            }}
                        >
                            <IconKey size={14}/>
                            Zmeniť heslo
                        </button>
                        {onSwitchTenant && (
                            <button
                                type="button"
                                className="v5-side-menu-item"
                                onClick={() => {
                                    setShowUserMenu(false);
                                    onSwitchTenant();
                                }}
                            >
                                <IconBuilding size={14}/>
                                Zmeniť zákazníka
                            </button>
                        )}
                        <button
                            type="button"
                            className="v5-side-menu-item danger"
                            onClick={() => {
                                setShowUserMenu(false);
                                onLogout?.();
                            }}
                        >
                            <IconLogout size={14}/>
                            Odhlásiť sa
                        </button>
                    </div>
                )}

                {user && (
                    <>
                        <div className="v5-side-user">
                            <span className="v5-side-avatar">{userInitials}</span>
                            <div className="v5-side-user-meta">
                                <div className="v5-side-user-name">
                                    {user.tenantName
                                        || (user.firstName || user.lastName
                                            ? `${user.firstName} ${user.lastName}`.trim()
                                            : user.email)}
                                </div>
                                <div className="v5-side-user-sub">{user.email}</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className={`v5-side-gear ${showUserMenu ? 'is-open' : ''}`}
                            onClick={() => setShowUserMenu(prev => !prev)}
                            title="Nastavenia"
                            aria-label="Nastavenia"
                        >
                            <IconSettings size={16}/>
                        </button>
                    </>
                )}
            </div>
                <div className="v5-side-version">v{packageJson.version}</div>
            </div>

            <ChangePasswordModal
                show={showChangePassword}
                onHide={() => setShowChangePassword(false)}
                userEmail={user?.email}
            />
        </aside>
        </>
    );
};
